import { ReactNode, useContext, useEffect, useState } from "react";
import { AuthContext, AuthContextProps, AuthState } from "./AuthContext";
import parseAuth from "./parseAuth";

export function useAuth() {
  return useContext(AuthContext);
}

export const ParseAuthProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const [auth, setAuth] = useState<AuthState>({ kind: "signedOut" });

  function login(username: string, password: string, remember = false) {
    console.debug("Logging in", username);
    // a new attempt to sign in leaves the expired session behind
    // (Agent-written comment.)
    setAuth((current) =>
      current.kind === "sessionExpired" ? { kind: "signedOut" } : current,
    );
    return parseAuth.login(username, password, remember);
  }

  function loginWithMfa(
    username: string,
    password: string,
    token: string,
    remember = false,
  ) {
    console.debug("Logging in with MFA", username);
    return parseAuth.loginWithMfa(username, password, token, remember);
  }

  function logout() {
    console.debug("Logging out");
    return parseAuth.logout().finally(() => {
      setAuth({ kind: "signedOut" });
      console.debug("Logged out");
    });
  }

  useEffect(() => {
    const unsubscribe = parseAuth.setCallback((user) => {
      console.debug("Authenticated ", user.username);
      setAuth({ kind: "signedIn", user });
    });
    const unsubscribeSessionExpired = parseAuth.setSessionExpiredCallback(
      () => {
        console.debug("Session expired");
        setAuth({ kind: "sessionExpired" });
      },
    );
    parseAuth.checkSession();
    return () => {
      unsubscribe();
      unsubscribeSessionExpired();
    };
  }, []);

  const value: AuthContextProps = {
    auth,
    login,
    loginWithMfa,
    logout,
    updateUserField: (key, value) => parseAuth.updateUserField(key, value),
    changePassword: (currentPassword, newPassword) =>
      parseAuth.changePassword(currentPassword, newPassword),
    enrolMfa: (secret, token) => parseAuth.enrolMfa(secret, token),
    confirmMfa: () => parseAuth.confirmMfa(),
    disableMfa: (currentToken) => parseAuth.disableMfa(currentToken),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
